// Rozet raporu PDF üreticisi — ONXFLOW görsel kimliğiyle uyumlu, ikonlu ve gruplandırılmış grafikli.
const TR_MONTHS = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

const periodLabel = (period) => {
  if (!period) return '';
  const [y, m] = period.split('-');
  return `${TR_MONTHS[parseInt(m, 10) - 1]} ${y}`;
};

// Renkler
const C = {
  gold: [212, 160, 23],
  silver: [148, 163, 184],
  bronze: [176, 104, 52],
  mention: [217, 70, 239],
  cyan: [6, 182, 212],
  violet: [124, 58, 237],
  amber: [245, 158, 11],
  dark: [17, 24, 39],
};

export async function generateBadgeReportPdf(data) {
  const { jsPDF } = await import('jspdf');
  const autoTableModule = await import('jspdf-autotable');
  const autoTable = autoTableModule.default;
  const { poppinsMedium, poppinsBold } = await import('./poppinsFont');

  const doc = new jsPDF('p', 'mm', 'a4');
  doc.addFileToVFS('Poppins-Medium.ttf', poppinsMedium);
  doc.addFileToVFS('Poppins-Bold.ttf', poppinsBold);
  doc.addFont('Poppins-Medium.ttf', 'Poppins', 'normal');
  doc.addFont('Poppins-Bold.ttf', 'Poppins', 'bold');
  doc.setFont('Poppins');

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 14;
  let y = 0;

  const tint = (rgb, amount) => rgb.map((c) => Math.round(255 - amount * (255 - c)));

  // ---- İkon çizim yardımcıları (vektör) ----
  const drawPolygon = (pts, style = 'F') => {
    const deltas = [];
    for (let i = 1; i < pts.length; i++) deltas.push([pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]]);
    doc.lines(deltas, pts[0][0], pts[0][1], [1, 1], style, true);
  };

  const drawTrophy = (cx, cy, s, color) => {
    doc.setFillColor(...color);
    // kupa ağzı (elips)
    doc.ellipse(cx, cy - s * 0.28, s * 0.42, s * 0.16, 'F');
    // kase gövdesi
    doc.triangle(cx - s * 0.42, cy - s * 0.28, cx + s * 0.42, cy - s * 0.28, cx, cy + s * 0.18, 'F');
    // kulplar
    doc.setLineWidth(s * 0.08);
    doc.setDrawColor(...color);
    doc.ellipse(cx - s * 0.5, cy - s * 0.18, s * 0.14, s * 0.16, 'S');
    doc.ellipse(cx + s * 0.5, cy - s * 0.18, s * 0.14, s * 0.16, 'S');
    // gövde ayağı
    doc.rect(cx - s * 0.06, cy + s * 0.14, s * 0.12, s * 0.22, 'F');
    // taban
    doc.rect(cx - s * 0.28, cy + s * 0.36, s * 0.56, s * 0.12, 'F');
  };

  const drawMedal = (cx, cy, s, color, number) => {
    // kurdele
    doc.setFillColor(...tint(color, 0.65));
    doc.triangle(cx - s * 0.34, cy - s * 0.55, cx - s * 0.02, cy - s * 0.55, cx - s * 0.02, cy - s * 0.02, 'F');
    doc.triangle(cx + s * 0.34, cy - s * 0.55, cx + s * 0.02, cy - s * 0.55, cx + s * 0.02, cy - s * 0.02, 'F');
    // madalya diski
    doc.setFillColor(...color);
    doc.circle(cx, cy + s * 0.18, s * 0.4, 'F');
    doc.setFillColor(...tint(color, 0.4));
    doc.circle(cx, cy + s * 0.18, s * 0.28, 'F');
    // numara
    if (number) {
      doc.setFont('Poppins', 'bold');
      doc.setFontSize(s * 2.6);
      doc.setTextColor(255, 255, 255);
      doc.text(String(number), cx, cy + s * 0.18 + s * 0.32, { align: 'center' });
    }
  };

  const drawStar = (cx, cy, s, color) => {
    const outer = s * 0.5;
    const inner = s * 0.22;
    const pts = [];
    let rot = -Math.PI / 2;
    const step = Math.PI / 5;
    for (let i = 0; i < 5; i++) {
      pts.push([cx + Math.cos(rot) * outer, cy + Math.sin(rot) * outer]); rot += step;
      pts.push([cx + Math.cos(rot) * inner, cy + Math.sin(rot) * inner]); rot += step;
    }
    doc.setFillColor(...color);
    drawPolygon(pts, 'F');
  };

  const drawIcon = (kind, cx, cy, s, color) => {
    if (kind === 'trophy') drawTrophy(cx, cy, s, color);
    else if (kind === 'star') drawStar(cx, cy, s, color);
    else drawMedal(cx, cy, s, color, kind === 'gold' ? 1 : kind === 'silver' ? 2 : 3);
  };

  const gen = new Date(data.generatedAt || Date.now());
  const genStr = gen.toLocaleString('tr-TR', {
    day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });

  // ===== HEADER =====
  doc.setFillColor(...C.dark);
  doc.rect(0, 0, pageWidth, 36, 'F');
  doc.setFillColor(...C.amber);
  doc.rect(0, 36, pageWidth, 1.2, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(20);
  doc.setFont('Poppins', 'bold');
  doc.text('ONXFLOW', margin, 15);
  doc.setFontSize(10);
  doc.setFont('Poppins', 'normal');
  doc.setTextColor(148, 163, 184);
  doc.text('Performans Rozetleri Raporu', margin, 23);
  doc.setFontSize(8);
  doc.setTextColor(251, 191, 36);
  doc.setFont('Poppins', 'bold');
  doc.text('Oluşturulma Tarihi', pageWidth - margin, 14, { align: 'right' });
  doc.setFont('Poppins', 'normal');
  doc.setTextColor(226, 232, 240);
  doc.text(genStr, pageWidth - margin, 20, { align: 'right' });
  y = 45;

  // ===== ÖZET KUTULARI (3 sütun, tek satır, büyük ikonlu) =====
  const boxes = [
    { label: 'Toplam Rozet', value: data.totalBadges || 0, icon: 'trophy', color: C.amber },
    { label: 'Toplam Dönem', value: data.totalPeriods || 0, icon: 'trophy', color: C.cyan },
    { label: 'Toplam Kategori', value: data.totalCategories || 0, icon: 'trophy', color: [22, 163, 74] },
  ];
  const cols = 3;
  const gap = 6;
  const boxW = (pageWidth - margin * 2 - gap * (cols - 1)) / cols;
  const boxH = 26;
  boxes.forEach((b, i) => {
    const bx = margin + i * (boxW + gap);
    const by = y;
    // kutu
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(...tint(b.color, 0.5));
    doc.setLineWidth(0.4);
    doc.roundedRect(bx, by, boxW, boxH, 2.5, 2.5, 'FD');
    // ikon karosu
    const tileS = 16;
    const tileX = bx + 4;
    const tileY = by + (boxH - tileS) / 2;
    doc.setFillColor(...tint(b.color, 0.14));
    doc.roundedRect(tileX, tileY, tileS, tileS, 2.5, 2.5, 'F');
    drawIcon(b.icon, tileX + tileS / 2, tileY + tileS / 2, tileS * 0.62, b.color);
    // metin
    const tx = tileX + tileS + 5;
    doc.setFontSize(8);
    doc.setFont('Poppins', 'normal');
    doc.setTextColor(100, 116, 139);
    doc.text(b.label, tx, by + 10);
    doc.setFontSize(19);
    doc.setFont('Poppins', 'bold');
    doc.setTextColor(...b.color);
    doc.text(String(b.value), tx, by + 20);
  });
  y += boxH + 12;

  // ===== BÖLÜM BAŞLIĞI yardımcı =====
  const sectionTitle = (title) => {
    doc.setFillColor(...C.amber);
    doc.roundedRect(margin, y, 3, 6, 1, 1, 'F');
    doc.setTextColor(...C.dark);
    doc.setFontSize(12);
    doc.setFont('Poppins', 'bold');
    doc.text(title, margin + 6, y + 5);
    y += 11;
  };

  // ===== DÖNEM BAZLI GRUPLANDIRILMIŞ ÇUBUK GRAFİK =====
  const perPeriod = data.perPeriod || [];
  if (perPeriod.length > 0) {
    sectionTitle('Dönem Bazlı Rozet Dağılımı');
    // Legend
    const series = [
      { key: 'gold', name: 'Altın', color: C.gold },
      { key: 'silver', name: 'Gümüş', color: C.silver },
      { key: 'bronze', name: 'Bronz', color: C.bronze },
      { key: 'mention', name: 'Mansiyon', color: C.mention },
    ];
    let lx = margin;
    series.forEach((s) => {
      doc.setFillColor(...s.color);
      doc.roundedRect(lx, y - 1, 4, 4, 1, 1, 'F');
      doc.setFontSize(8);
      doc.setFont('Poppins', 'normal');
      doc.setTextColor(71, 85, 105);
      doc.text(s.name, lx + 5.5, y + 2.5);
      lx += 5.5 + doc.getTextWidth(s.name) + 8;
    });
    y += 8;

    const chartH = 40;
    const baseY = y + chartH;
    const chartW = pageWidth - margin * 2;
    const maxV = Math.max(1, ...perPeriod.flatMap((p) => [p.gold, p.silver, p.bronze, p.mention]));
    // eksen
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.3);
    doc.line(margin, baseY, margin + chartW, baseY);

    const n = perPeriod.length;
    const groupGap = 10;
    const groupW = Math.min(70, (chartW - groupGap * (n + 1)) / n);
    const innerGap = 2;
    const barW = (groupW - innerGap * 3) / 4;

    perPeriod.forEach((p, gi) => {
      const gx = margin + groupGap + gi * (groupW + groupGap);
      series.forEach((s, si) => {
        const val = p[s.key] || 0;
        const bx = gx + si * (barW + innerGap);
        const h = Math.max(val > 0 ? 2 : 0, (val / maxV) * chartH);
        if (val > 0) {
          doc.setFillColor(...s.color);
          doc.roundedRect(bx, baseY - h, barW, h, 0.8, 0.8, 'F');
          doc.setFontSize(6.5);
          doc.setFont('Poppins', 'bold');
          doc.setTextColor(...s.color);
          doc.text(String(val), bx + barW / 2, baseY - h - 1.5, { align: 'center' });
        }
      });
      // dönem etiketi
      doc.setFontSize(8);
      doc.setFont('Poppins', 'bold');
      doc.setTextColor(71, 85, 105);
      doc.text(periodLabel(p.period), gx + groupW / 2, baseY + 5, { align: 'center' });
    });
    y = baseY + 14;
  }

  // ===== KULLANICI BAZLI TABLO =====
  const users = data.users || [];
  if (users.length > 0) {
    if (y > pageHeight - 40) { doc.addPage(); y = margin; }
    sectionTitle('Kullanıcı Bazlı Rozetler');

    // sütun toplamları
    const sum = (k) => users.reduce((acc, u) => acc + (u[k] || 0), 0);
    const gSum = sum('gold'), sSum = sum('silver'), bSum = sum('bronze'), mSum = sum('mention'), tSum = sum('total');

    // ikon sütunu -> kind eşlemesi (head hücresine ikon çizmek için)
    const headIcon = { 1: { kind: 'gold', color: C.gold }, 2: { kind: 'silver', color: C.silver }, 3: { kind: 'bronze', color: C.bronze }, 4: { kind: 'star', color: C.mention } };

    autoTable(doc, {
      startY: y,
      head: [['Kullanıcı', 'Altın', 'Gümüş', 'Bronz', 'Mansiyon', 'Toplam']],
      body: users.map((u) => [u.userName, u.gold, u.silver, u.bronze, u.mention, u.total]),
      foot: [
        [
          { content: 'Alt Toplam', styles: { halign: 'left' } },
          { content: gSum, styles: { halign: 'center' } },
          { content: sSum, styles: { halign: 'center' } },
          { content: bSum, styles: { halign: 'center' } },
          { content: mSum, styles: { halign: 'center' } },
          { content: tSum, styles: { halign: 'center' } },
        ],
      ],
      styles: { font: 'Poppins', fontSize: 8.5, cellPadding: 2.5, textColor: [51, 65, 85] },
      headStyles: { font: 'Poppins', fontStyle: 'bold', fillColor: C.dark, textColor: [255, 255, 255], fontSize: 8, minCellHeight: 13, valign: 'bottom' },
      footStyles: { font: 'Poppins', fontStyle: 'bold', fillColor: [241, 245, 249], textColor: [17, 24, 39], fontSize: 8.5 },
      columnStyles: {
        0: { halign: 'left', fontStyle: 'bold' },
        1: { halign: 'center' }, 2: { halign: 'center' }, 3: { halign: 'center' },
        4: { halign: 'center' }, 5: { halign: 'center', fontStyle: 'bold', textColor: [180, 83, 9] },
      },
      alternateRowStyles: { fillColor: [250, 250, 249] },
      margin: { left: margin, right: margin },
      didDrawCell: (hook) => {
        if (hook.section === 'head' && headIcon[hook.column.index]) {
          const ic = headIcon[hook.column.index];
          const cx = hook.cell.x + hook.cell.width / 2;
          const cyTop = hook.cell.y + 4.5;
          drawIcon(ic.kind, cx, cyTop, 5, ic.color);
        }
      },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  // ===== FOOTER =====
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.3);
    doc.line(margin, pageHeight - 12, pageWidth - margin, pageHeight - 12);
    doc.setFontSize(7);
    doc.setFont('Poppins', 'normal');
    doc.setTextColor(148, 163, 184);
    doc.text(`OnxFlow Rozet Raporu · ${genStr}`, margin, pageHeight - 7);
    doc.text(`Sayfa ${i} / ${pageCount}`, pageWidth - margin, pageHeight - 7, { align: 'right' });
  }

  doc.save(`OnxFlow_Rozet_Raporu_${gen.toISOString().slice(0, 10)}.pdf`);
}
